import React, { useState, useEffect, useCallback, useRef } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';
import {
  buildFlashlightRadialGradientBackgroundAtPercent,
  normalizeFlashlightColorForDisplay,
} from '../constants/flashlightColors';

const FLASHLIGHT_SWITCH_SOUND = '/sounds/switch.mp3';

function isExcludedFlashlightTarget(target, nativeEvent) {
  if (typeof Element === 'undefined') {
    return false;
  }

  const exclusionSelector = [
    'button',
    'a[href]',
    'input',
    'textarea',
    'select',
    '[role="button"]',
    '[contenteditable="true"]',
    '[data-no-minigame-flashlight="true"]',
  ].join(', ');

  const path = typeof nativeEvent?.composedPath === 'function' ? nativeEvent.composedPath() : [];
  for (const node of path) {
    if (node instanceof Element && node.matches(exclusionSelector)) {
      return true;
    }
  }

  if (target instanceof Element) {
    return Boolean(target.closest(exclusionSelector));
  }

  return false;
}

/**
 * @param {{ x: number, y: number } | null} beam client coords
 * @param {DOMRect} rect play arena bounds
 */
function buildDarknessBackground(beam, rect) {
  if (!beam || !(rect.width > 0) || !(rect.height > 0)) {
    return 'rgba(0, 0, 0, 0.99)';
  }
  const xPct = ((beam.x - rect.left) / rect.width) * 100;
  const yPct = ((beam.y - rect.top) / rect.height) * 100;
  return `radial-gradient(circle at ${xPct}% ${yPct}%, rgba(0, 0, 0, 0.00) 0px, rgba(0, 0, 0, 0.22) 75px, rgba(0, 0, 0, 0.82) 150px, rgba(0, 0, 0, 0.99) 280px)`;
}

/**
 * @param {{ x: number, y: number } | null} beam client coords
 * @param {DOMRect} rect
 * @param {string} colorKey
 */
function buildScopedColoredBeamBackground(beam, rect, colorKey) {
  if (!beam || !(rect.width > 0) || !(rect.height > 0)) {
    return 'transparent';
  }
  const xPct = ((beam.x - rect.left) / rect.width) * 100;
  const yPct = ((beam.y - rect.top) / rect.height) * 100;
  return buildFlashlightRadialGradientBackgroundAtPercent(colorKey, xPct, yPct);
}

/**
 * Darkness + drag flashlight scoped to a play arena element (not the full page).
 * Renders overlay layers only — place inside the play arena container.
 */
export default function ExploreUnknownMinigameFlashlightDarkness({
  playArenaRef,
  flashlightColorKey = 'white',
}) {
  const [beam, setBeam] = useState(null);
  const [arenaRect, setArenaRect] = useState(null);
  const beamRef = useRef(null);
  const colorKey = normalizeFlashlightColorForDisplay(flashlightColorKey);

  const syncArenaRect = useCallback(() => {
    const el = playArenaRef?.current;
    if (!el) {
      setArenaRect(null);
      return;
    }
    setArenaRect(el.getBoundingClientRect());
  }, [playArenaRef]);

  useEffect(() => {
    beamRef.current = beam;
  }, [beam]);

  const clearBeam = useCallback(() => {
    if (beamRef.current != null) {
      playSoundEffect(FLASHLIGHT_SWITCH_SOUND);
    }
    setBeam(null);
  }, []);

  useEffect(() => {
    const root = playArenaRef?.current;
    if (!root) {
      throw new Error('ExploreUnknownMinigameFlashlightDarkness: playArenaRef must be attached');
    }

    syncArenaRect();

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (!root.contains(e.target)) return;
      if (isExcludedFlashlightTarget(e.target, e)) return;
      e.preventDefault();
      playSoundEffect(FLASHLIGHT_SWITCH_SOUND);
      setBeam({ x: e.clientX, y: e.clientY });
      syncArenaRect();
    };

    const onDragStart = (e) => {
      e.preventDefault();
    };

    root.addEventListener('pointerdown', onPointerDown, true);
    root.addEventListener('dragstart', onDragStart);
    return () => {
      root.removeEventListener('pointerdown', onPointerDown, true);
      root.removeEventListener('dragstart', onDragStart);
    };
  }, [playArenaRef, syncArenaRect]);

  useEffect(() => {
    if (!beam) return undefined;

    const onMove = (e) => {
      setBeam({ x: e.clientX, y: e.clientY });
      syncArenaRect();
    };

    const onUp = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      clearBeam();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [beam, clearBeam, syncArenaRect]);

  useEffect(() => {
    const el = playArenaRef?.current;
    if (!el) return undefined;
    const bump = () => syncArenaRect();
    window.addEventListener('resize', bump);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(bump);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', bump);
      if (ro) ro.disconnect();
    };
  }, [playArenaRef, syncArenaRect]);

  useEffect(
    () => () => {
      if (beamRef.current != null) {
        playSoundEffect(FLASHLIGHT_SWITCH_SOUND);
      }
    },
    []
  );

  if (!arenaRect) {
    return (
      <div
        aria-hidden="true"
        className="explore-unknown-minigame-darkness-overlay explore-unknown-minigame-darkness-overlay--scoped"
        style={{ background: 'rgba(0, 0, 0, 0.99)' }}
      />
    );
  }

  return (
    <>
      <div
        aria-hidden="true"
        className="explore-unknown-minigame-darkness-overlay explore-unknown-minigame-darkness-overlay--scoped"
        style={{ background: buildDarknessBackground(beam, arenaRect) }}
      />
      {beam ? (
        <div
          aria-hidden="true"
          className="explore-unknown-minigame-colored-beam explore-unknown-minigame-colored-beam--scoped"
          style={{ background: buildScopedColoredBeamBackground(beam, arenaRect, colorKey) }}
        />
      ) : null}
    </>
  );
}
